import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getResourceBookmarks as getResourceBookmarksApi, ResourceBookmark } from "../../api";

export const hydrateResourceBookmarks = createAsyncThunk<ResourceBookmark[], string, { rejectValue: string }>(
    "resourceBookmarks/hydrateResourceBookmarks",
    async (userId, thunkApi) => {
        try {
            return await getResourceBookmarksApi(userId);
        } catch (error) {
            return thunkApi.rejectWithValue(
                error instanceof Error ? error.message : "Failed to hydrate resource bookmarks",
            );
        }
    },
);

export type ResourceBookmarksState = {
    bookmarks: ResourceBookmark[];
    status: "idle" | "pending" | "succeeded" | "failed";
    error: string | null;
};

const initialState: ResourceBookmarksState = {
    bookmarks: [],
    status: "idle",
    error: null,
};

const resourceBookmarksSlice = createSlice({
    name: "resourceBookmarks",
    initialState,
    reducers: {
        toggleResourceBookmark: (state, action: PayloadAction<ResourceBookmark>) => {
            const { userId, resourceId } = action.payload;
            const alreadyBookmarked = state.bookmarks.some((bookmark) => bookmark.resourceId === resourceId);

            if (alreadyBookmarked) {
                state.bookmarks = state.bookmarks.filter((bookmark) => bookmark.resourceId !== resourceId);
                return;
            }

            state.bookmarks.push({ userId, resourceId });
        },

        clearResourceBookmarks: (state) => {
            state.status = "idle";
            state.bookmarks = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(hydrateResourceBookmarks.pending, (state) => {
                state.status = "pending";
                state.error = null;
            })
            .addCase(hydrateResourceBookmarks.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = null;
                state.bookmarks = action.payload;
            })
            .addCase(hydrateResourceBookmarks.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? action.error.message ?? "Failed to hydrate bookmarks";
            });
    },
});

export const { toggleResourceBookmark, clearResourceBookmarks } = resourceBookmarksSlice.actions;

export default resourceBookmarksSlice.reducer;
